/**
 * Room-code shape: V4.1.T5 (D6). The SINGLE definition for the
 * short, human-readable room code used in `/r/:code` links and (future T19)
 * join-by-code. Both client and server import from here so the charset,
 * length, and validation can never drift apart.
 *
 * Charset: 30 unambiguous chars, digits 2-9 (drops 0 and 1) plus A-Z minus
 * I, L, O, U. Length 4 → 30^4 = 810,000 codes.
 *
 * Canonical room ids are UPPERCASE. Join/lookup is CASE-INSENSITIVE: callers
 * MUST `normalizeRoomCode` (trim + uppercase) the incoming code at every
 * join/lookup entry point before validating or looking the room up.
 */

/** Unambiguous room-code alphabet (no 0,1,I,L,O,U). 30 chars. */
export const ROOM_CODE_CHARSET = "23456789ABCDEFGHJKMNPQRSTVWXYZ";

/** Room codes are exactly this many characters. */
export const ROOM_CODE_LENGTH = 4;

/** Exact validator: 4 uppercase chars drawn from ROOM_CODE_CHARSET. */
const ROOM_CODE_RE = /^[23456789ABCDEFGHJKMNPQRSTVWXYZ]{4}$/;

/** True iff `id` is a canonical room code (4 chars, charset, uppercase). */
export function isValidRoomId(id: string): boolean {
  return ROOM_CODE_RE.test(id);
}

/**
 * Normalize a user-supplied room code for case-insensitive lookup:
 * trim surrounding whitespace and uppercase. Does NOT validate, call
 * `isValidRoomId` on the result before using it as a lookup key.
 *
 * Coerces non-string input to a string first. The TS signature says `string`,
 * but the WS HELLO frame is an unchecked `JSON.parse(...)` cast, so a malicious
 * client can send a number/object/array `roomId`. Coercing (instead of calling
 * `.trim()` on a non-string, which throws `TypeError: input.trim is not a
 * function`) keeps a bad frame from becoming an unhandled rejection that crashes
 * the process. A coerced non-string never matches a real 4-char room code, so
 * the lookup safely falls through to ROOM_GONE.
 */
export function normalizeRoomCode(input: string): string {
  return String(input ?? "")
    .trim()
    .toUpperCase();
}
